/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './PDFViewer.css';

// Initialize PDF.js
const initPDFJS = () => {
  if (window.pdfjsLib) {
    // Set worker source
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.0.375/pdf.min.mjs';
    return window.pdfjsLib;
  }
  return null;
};

const PDFViewer = ({ url, fileName, requestBlob }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [pageRendering, setPageRendering] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  
  // Initialize PDF.js and load the PDF
  useEffect(() => {
    let pdfjsLib = null;
    let pdfLoadingTask = null;
    
    const loadPDF = async (urlData, useData = false) => {
      try {
        setLoading(true);
        
        // Initialize PDF.js
        pdfjsLib = initPDFJS();
        
        if (!pdfjsLib) {
          // Wait for PDF.js to load
          await new window.Promise((resolve) => {
            const checkPDFJS = setInterval(() => {
              if (window.pdfjsLib) {
                pdfjsLib = initPDFJS();
                clearInterval(checkPDFJS);
                resolve();
              }
            }, 100);
            
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkPDFJS);
              resolve();
            }, 5000);
          });
          
          // Check if PDF.js is available
          if (!pdfjsLib) {
            throw new Error('PDF.js library failed to load');
          }
        }
        let isPDFOK = false;
        // Fetch the PDF
        if (useData) {
          try {
            const pdfData = atob(urlData);
            pdfLoadingTask = pdfjsLib.getDocument({ data: pdfData });
            isPDFOK = true;
          } catch (e) {
            // console.error('Error decoding base64 data:', e);
          }
        } else {
          isPDFOK = true;
          pdfLoadingTask = pdfjsLib.getDocument(urlData);
        }
        if (!isPDFOK) {
          throw new Error('Failed to load PDF');
        }
        let pdf;
        try {
          pdf = await pdfLoadingTask.promise;
        } catch (error) {
          throw new Error('Failed to load PDF document');
        }
        
        setPdfDoc(pdf);
        setPageCount(pdf.numPages);
        setLoading(false);
      } catch (err) {
        // console.error('Error loading PDF:', err);
        setError(`Failed to load PDF: ${err.message}`);
        setLoading(false);
      }
    };

    const isUseData = !url || url === '';
    if (isUseData && requestBlob) {
      loadPDF(requestBlob, true);
    } else if (url && url.length > 0) {
      loadPDF(url, false);
    }
    // Cleanup
    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
      }
      if (pdfLoadingTask) {
        pdfLoadingTask.destroy();
      }
    };
  }, [url, requestBlob]);
  
  // Render the current page whenever the document, page, or zoom changes.
  // Driving this from an effect (rather than calling renderPage directly
  // after setState) guarantees the canvas is mounted — during loading the
  // component renders a spinner and canvasRef is null.
  useEffect(() => {
    if (!pdfDoc) return;

    const renderPage = async () => {
      setPageRendering(true);

      try {
        // Get the page
        const page = await pdfDoc.getPage(pageNum);

        // Get the canvas
        const canvas = canvasRef.current;
        if (!canvas) {
          // console.error('Canvas element not found');
          setPageRendering(false);
          return;
        }

        const ctx = canvas.getContext('2d');

        // Get the viewport
        const viewport = page.getViewport({ scale });

        // Set canvas dimensions
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Cancel any in-flight render before starting a new one — PDF.js
        // throws if two renders target the same canvas concurrently
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }
        const renderTask = page.render({
          canvasContext: ctx,
          viewport: viewport
        });
        renderTaskRef.current = renderTask;

        await renderTask.promise;

        renderTaskRef.current = null;
        setPageRendering(false);
      } catch (err) {
        if (err && err.name === 'RenderingCancelledException') {
          // Superseded by a newer render — the newer one resets state
          return;
        }
        // console.error('Error rendering page:', err);
        setError(`Failed to render page: ${err.message}`);
        setPageRendering(false);
      }
    };

    renderPage();
  }, [pdfDoc, pageNum, scale]);

  // Go to previous page
  const prevPage = () => {
    if (pageNum <= 1) return;
    setPageNum(pageNum - 1);
  };

  // Go to next page
  const nextPage = () => {
    if (pageNum >= pageCount) return;
    setPageNum(pageNum + 1);
  };

  // Zoom in
  const zoomIn = () => {
    setScale(Math.min(scale + 0.25, 3.0));
  };

  // Zoom out
  const zoomOut = () => {
    setScale(Math.max(scale - 0.25, 0.5));
  };

  // Reset zoom
  const resetZoom = () => {
    setScale(1.0);
  };

  // Download PDF
  const downloadPdf = () => {
    if (requestBlob) {
      const byteCharacters = atob(requestBlob);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } else if (url) {
      window.open(url, '_blank');
    }
  };

  // Print PDF
  const printPdf = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Print - ${fileName}</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              @media print { body { margin: 0; } img { max-width: 100%; max-height: 100%; } }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" onload="window.print(); window.close();" />
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="PDFViewer-loading">
        <div className="PDFViewer-spinner"></div>
        <p>Loading PDF...</p>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="PDFViewer-error">
        <div className="PDFViewer-error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
            <path d="M12 8V12" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 16H12.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <p className="PDFViewer-error-message">{error}</p>
      </div>
    );
  }
  
  return (
    <div className="PDFViewer">
      <div className="PDFViewer-container">
        <canvas ref={canvasRef} className="PDFViewer-canvas"></canvas>
      </div>
      
      <div className="PDFViewer-controls">
        <div className="PDFViewer-page-controls">
          <button 
            className="PDFViewer-btn" 
            onClick={prevPage}
            disabled={pageNum <= 1 || pageRendering}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <span className="PDFViewer-page-info">
            Page {pageNum} of {pageCount}
          </span>
          
          <button 
            className="PDFViewer-btn" 
            onClick={nextPage}
            disabled={pageNum >= pageCount || pageRendering}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="PDFViewer-zoom-controls">
          <button
            className="PDFViewer-btn"
            onClick={zoomOut}
            disabled={scale <= 0.5 || pageRendering}
            title="Zoom Out"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M8 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          <span className="PDFViewer-zoom-level">{Math.round(scale * 100)}%</span>

          <button
            className="PDFViewer-btn"
            onClick={zoomIn}
            disabled={scale >= 3.0 || pageRendering}
            title="Zoom In"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
              <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M11 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M8 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          <button
            className="PDFViewer-btn"
            onClick={resetZoom}
            disabled={scale === 1.0 || pageRendering}
            title="Reset Zoom"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C8.5 3 5.5 5 4 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M3 3V8H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

        </div>

        <div className="PDFViewer-action-controls">
          <button
            className="PDFViewer-btn"
            onClick={printPdf}
            disabled={pageRendering}
            title="Print"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className="PDFViewer-btn"
            onClick={downloadPdf}
            title="Download"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* File info hidden — fileName is a default placeholder, not the actual filename */}
      </div>
    </div>
  );
};

PDFViewer.propTypes = {
  url: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  requestBlob: PropTypes.string
};

PDFViewer.defaultProps = {
  fileName: 'Document'
};

export default PDFViewer;
